/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable max-len */
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useResizeDetector } from 'react-resize-detector';
import {
  Accordion,
  AccordionSection,
  Layout,
  ProgressIndicator,
  Text,
  ToggleSwitch,
  Tabs,
} from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import { AnimatePresence } from 'framer-motion';
import ReactHtmlParser from 'react-html-parser';
import { useGoogleConsent } from '@oneaudi/two-click';
import {
  SYS_BORDER_RADIUS_MD,
  SYS_SPACE_FIXED_150,
  SYS_SPACE_FIXED_200,
  SYS_SPACE_FIXED_350,
} from '@oneaudi/unified-web-common';
import { DISPLAY_SIZE } from '../../common/global.style.constants';
import SearchGroup from '../SearchGroup/SearchGroup';
import searchStateContext from '../../../contexts/map-context';
import MapAutocompletion from '../MapAutocompletion/MapAutocompletion';
import Map from '../Map/Map';
import { MobileDealerDetails } from '../DealerDetails/MobileDealerDetails';
import { SearchResultsGroup } from '../SearchResultsGroup/SearchResultsGroup';
import { BackToResults } from '../BackToResults/BackToResults';
import { useDisplayResults } from './MobileLayoutUtils';
import { LoadMoreButton } from '../LoadMoreButton/LoadMoreButton';
import { BackToSearch } from '../BackToSearch/BackToSearch';
import { messages } from '../../../i18n/messages';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import AnimatedContainer from '../../common/AnimatedContainer/AnimatedContainer';
import FiltersOverlay from '../FiltersOverlay/FiltersOverlay';
import { MapToggleContainer } from '../../common/TwoClickConsentModal/style';
import { MobileFlyout } from '../Flyout/MobileFlyout';
import MyAudiModal from '../MyAudiModal/MyAudiModal';
import { DEFAULT_CENTER_COORDINATES } from '../Map/MapUtils';
import { UnifiedTheme } from '../../../FeatureApp';

interface ContainerProps {
  isLoaded: boolean;
  oneHeader: boolean;
  hasSelectedDealerId?: boolean;
  height?: string;
  headerMobileView?: boolean;
}

interface MobileContainerProps {
  isListView?: boolean;
  oneHeader?: boolean;
}

interface MobileLayoutProps {
  revokeConsent?: () => void | undefined;
}

export const MobileLayout = (props: MobileLayoutProps) => {
  const [displayResults, onLoadMoreClick] = useDisplayResults();
  const [lastViewedMapCenter, setLastViewedMapCenter] = useState(DEFAULT_CENTER_COORDINATES);
  const { revokeConsent } = props;
  const consent = useGoogleConsent();

  const {
    isAutocompletionOpen,
    results,
    selectedDealerId,
    activeMobileView,
    isFiltersOverlayOpen,
    dealerReviews,
    ratingSummary,
    isFlyoutOpen,
    isGoogleMapsApiLoaded,
    twoClickData,
    twoClickConsentRequired,
    ctaBarButtonConfig,
    showModal,
    mapCenterCoordinates,
    oneHeader,
    headerMobileView,
  } = searchStateContext.useMapState();

  const dispatch = searchStateContext.useMapDispatch();
  const { asideToggleCopy, asideToggleLabel, asideToggleHeadline } = twoClickData;
  const [toggle, setToggle] = useState(consent);
  const [openAccordion, setOpenAccordion] = useState(false);

  const { height: searchBarHeight, ref: searchBarRef } = useResizeDetector({ handleWidth: false });

  const containerResultsRef = useRef<HTMLDivElement>(null);

  const mapView = useI18n(messages.map_view);
  const listView = useI18n(messages.list_view);
  const listViewPlaceholder = useI18n(messages.list_view_empty_result_placeholder);

  const onTabSelect = (id: string) => {
    dispatch({ type: ActionTypes.setActiveMobileView, payload: id });
  };

  useEffect(() => {
    const isCenterNew =
      lastViewedMapCenter.lat !== mapCenterCoordinates.lat ||
      lastViewedMapCenter.lng !== mapCenterCoordinates.lng;
    if (activeMobileView === 'map' && isCenterNew) {
      setLastViewedMapCenter(mapCenterCoordinates);
      window.dispatchEvent(new Event('resize'));
    }
  }, [activeMobileView]);

  // Current iteration of WYSISYG editor in Face editor does not allow author to add target attribute, so this hook is in place to add the blank target. Ok to remove if feature is ever added to editor in the future.
  useEffect(() => {
    const links = document.querySelectorAll('.revoke-consent > p > a');
    links.forEach((item) => item.setAttribute('target', '_blank'));
  }, []);

  useEffect(() => {
    setToggle(consent);
  }, [consent]);

  const setHeight = () => {
    return '100%';
  };

  return (
    <>
      {!isGoogleMapsApiLoaded && (
        <LoaderContainer>
          <ProgressIndicator
            type="infinite"
            variant="spinner"
            data-test-id="mobile-layout-component"
          />
        </LoaderContainer>
      )}

      {showModal && <MyAudiModal ctaBarButtonConfig={ctaBarButtonConfig} />}

      <Container
        isLoaded={isGoogleMapsApiLoaded}
        oneHeader={oneHeader}
        hasSelectedDealerId={!!selectedDealerId}
        height={setHeight()}
        headerMobileView={headerMobileView}
      >
        <MobileContainer
          aria-label="dealer-search-mobile"
          isListView={activeMobileView === 'list'}
          oneHeader={oneHeader}
        >
          <span ref={searchBarRef}>
            {!selectedDealerId && (
              <SearchBarContainer data-test-id="search-bar-container">
                <SearchGroup />
              </SearchBarContainer>
            )}

            {!selectedDealerId && !isAutocompletionOpen && (
              <CustomTabs
                id="tab-group"
                data-test-id="result-navigation"
                items={[
                  {
                    active: activeMobileView === 'map',
                    label: mapView,
                    onClick: () => {
                      onTabSelect('map');
                    },
                    content: <></>,
                  },
                  {
                    active: activeMobileView === 'list',
                    label: listView,
                    onClick: () => {
                      onTabSelect('list');
                    },
                    content: <></>,
                  },
                ]}
                size="sm"
                scrollButtons={{
                  left: {
                    ariaLabel: 'Scroll Back',
                  },
                  right: {
                    ariaLabel: 'Scroll Forward',
                  },
                }}
              />
            )}
          </span>
          {isAutocompletionOpen && (
            <AutocompletionContainer
              topOffset={searchBarHeight || 0}
              data-test-id="autocompletion-container"
              oneHeader={oneHeader}
            >
              <MapAutocompletion data-test-id="component-map-autocompletion" />
            </AutocompletionContainer>
          )}
          <BackToResults />
          {!isAutocompletionOpen && (
            <MapContainer
              topOffset={searchBarHeight || 53}
              data-test-id="map-container"
              hasSelectedDealerId={!!selectedDealerId}
              show={activeMobileView === 'map' || !!selectedDealerId}
              className="map-container-mobile"
            >
              {!twoClickConsentRequired || consent ? <Map /> : null}
            </MapContainer>
          )}

          {results && !isAutocompletionOpen && !selectedDealerId && activeMobileView === 'list' && (
            <>
              <SearchResultsGroup
                ref={containerResultsRef}
                results={displayResults}
                data-test-id="component-search-results-group"
              />

              <LoadMoreButton
                totalNumOfResults={results.length}
                displayResultsLength={displayResults.length}
                onClick={onLoadMoreClick}
              />
              <BackToSearch
                searchBarRef={searchBarRef}
                containerRef={containerResultsRef}
                displayResultsLength={displayResults.length}
              />
            </>
          )}

          {!results &&
            !isAutocompletionOpen &&
            !selectedDealerId &&
            activeMobileView === 'list' && (
              <Span>
                <Text
                  tag="p"
                  variant={{ order: '2', style: 'normal', type: 'copy' }}
                  data-test-id="empty-result-text"
                  margin={[0, SYS_SPACE_FIXED_350, SYS_SPACE_FIXED_200]}
                >
                  {listViewPlaceholder}
                </Text>
              </Span>
            )}

          {selectedDealerId && (
            <MobileDealerDetails
              dealerId={selectedDealerId}
              data-test-id="component-mobile-dealer-details"
            />
          )}

          {selectedDealerId && isFlyoutOpen && (dealerReviews?.length || ratingSummary) && (
            <MobileFlyout data-test-id="component-mobile-flyout" />
          )}

          {twoClickConsentRequired && (
            <AccordionContainer oneHeader={oneHeader}>
              <Accordion className="mobile-accordion">
                <AccordionSection
                  headingTag="h3"
                  label={asideToggleHeadline}
                  open={openAccordion}
                  onToggle={() => {
                    setOpenAccordion(!openAccordion);
                  }}
                >
                  <MapToggleContainer isLegacy={process.env.IS_LEGACY}>
                    <Div>
                      <ToggleSwitch
                        aria-labelledby="two-click-accordion"
                        id="toggle"
                        name="two-click-accordion"
                        value={toggle}
                        checked={toggle}
                        onChange={() => {
                          if (revokeConsent) {
                            setToggle(false);
                            revokeConsent();
                          }
                        }}
                        label=""
                      />
                      <Text
                        className="revoke-consent-label"
                        variant={{ order: '1', type: 'copy', style: 'bold' }}
                      >
                        {asideToggleLabel}
                      </Text>
                    </Div>
                    <Text tag="span" className="revoke-consent">
                      {ReactHtmlParser(asideToggleCopy)}
                    </Text>
                  </MapToggleContainer>
                </AccordionSection>
              </Accordion>
            </AccordionContainer>
          )}
        </MobileContainer>
        <AnimatePresence>
          <React.Fragment key="test">
            {isFiltersOverlayOpen &&
              (oneHeader ? (
                <FiltersOverlay data-test-id="component-filters-overlay" />
              ) : (
                <AnimatedContainer>
                  <FiltersOverlay data-test-id="component-filters-overlay" />
                </AnimatedContainer>
              ))}
          </React.Fragment>
        </AnimatePresence>
      </Container>
    </>
  );
};
export default MobileLayout;

const device = {
  desktop: `(min-width:${DISPLAY_SIZE.DESKTOP})`,
  largeDesktop: `(min-width:${DISPLAY_SIZE.LARGE_DESKTOP})`,
  smallDesktop: `(min-width:${DISPLAY_SIZE.SMALL_DESKTOP})`,
  mobile: `(max-width:${DISPLAY_SIZE.MOBILE})`,
  tablet: `(min-width: ${DISPLAY_SIZE.TABLET})`,
  smallTablet: `(max-width: ${DISPLAY_SIZE.TABLET})`,
};

const Container = styled.div<ContainerProps>`
  visibility: ${(props) => (props.isLoaded ? 'visible' : 'hidden')};
  width: 100%;

  ${(props) =>
    props.oneHeader && !props.headerMobileView
      ? `max-width: 447px; position: relative;`
      : `max-width: 100%; position: relative;`};

  height: ${(props) => props.height};

  ${(props) =>
    !props.hasSelectedDealerId &&
    `  .map-container-mobile {
    height: 100vh;
    max-height: 613px;
    min-height: unset;
  }`};

  @media (max-width: 767px) {
    height: unset;
  }

  @media (max-width: 1023px) {
    max-width: 100%;
  }
`;

const LoaderContainer = styled.div`
  align-items: center;
  background-color: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  display: flex;
  height: 100%;
  justify-content: center;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  z-index: 1;
`;

const MobileContainer = styled.div<MobileContainerProps>`
  ${(props) => props.oneHeader && `border-radius: ${SYS_BORDER_RADIUS_MD}`};
  background-color: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  display: block;
  flex-direction: column;
  flex-wrap: wrap;
  min-height: ${(props) => (props.isListView ? 'auto' : '100%')};
  height: ${(props) => (props.oneHeader ? `unset` : '100%')};
  position: relative;
  width: 100%;
  z-index: 0;

  .mobile-accordion {
    > div > div {
      padding: 0;
      > div {
        display: grid;
        grid-template-rows: auto auto;
      }
    }

    h3 {
      margin: 0;
      button {
        border-bottom: none;
      }
    }
    #two-click-accordion__panel {
      background-color: ${(props: UnifiedTheme) =>
        props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};

      > div {
        margin: 0 auto;
        min-width: 375px;
        padding: 1%;
      }
    }
  }
  @media (max-width: 1023px) {
    border-radius: ${(props) => props.oneHeader && '0px'};
  }
`;

const AccordionContainer = styled.div<{ oneHeader: boolean }>`
  background: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  width: 100%;
  ${(props) =>
    props.oneHeader &&
    `position: sticky; bottom: 0; border-bottom-left-radius: ${SYS_BORDER_RADIUS_MD}; 
      border-bottom-right-radius: ${SYS_BORDER_RADIUS_MD}`};

  @media (max-width: 1023px) {
    border-radius: ${(props) => props.oneHeader && '0px'};
  }
`;

const SearchBarContainer = styled.div`
  position: relative;
  width: 100%;
  z-index: 50;
`;

const MapContainer = styled(Layout)<{
  topOffset: number;
  hasSelectedDealerId?: boolean;
  show: boolean;
}>`
  position: relative;
  min-height: ${({ hasSelectedDealerId }) => (hasSelectedDealerId ? '127px' : `542px`)};
  display: ${({ show }) => (show ? 'block' : 'none')};
`;

const AutocompletionContainer = styled.div<{ topOffset: number; oneHeader: boolean }>`
  top: ${(props) => props.topOffset}px;
  position: unset;
  height: ${({ oneHeader }) => (oneHeader ? '630px' : '100%')};
  left: 0;
  width: 100%;
  z-index: 40;
  background-color: ${(props: UnifiedTheme) =>
    props.theme.sys.color.canvas[200] || props.theme.sys.color.canvas.default};
  ${(props) => props.oneHeader && `border-radius: ${SYS_BORDER_RADIUS_MD}`};

  @media ${device.tablet} {
    position: ${(oneHeader) => !oneHeader && 'absolute'};
  }
  @media (max-width: 1023px) {
    border-radius: ${(props) => props.oneHeader && '0px'};
  }
`;

const Div = styled.div`
  align-self: center;
  display: inline-flex;
  gap: ${SYS_SPACE_FIXED_150};
`;

const CustomTabs = styled(Tabs)`
  padding: 0 ${SYS_SPACE_FIXED_350} ${SYS_SPACE_FIXED_350};
  #tab-group-tab-pnl-0,
  #tab-group-tab-pnl-1 {
    display: none;
  }
`;

const Span = styled.span`
  height: 100vh;
  max-height: 542px;
  display: block;
`;
